import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, message, Row, Select, Spin, Table } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../auth';

const { Option } = Select;

export default function ManualOrder() {
    const [form] = Form.useForm();
    const [offers, setOffers] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingOffers, setLoadingOffers] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Load offers for the user's company
        if (user && user.company) {
            setLoadingOffers(true);
            api.getOffers()
                .then(companyOffers => {
                    const activeOffers = (companyOffers || [])
                        .filter(offer => !offer.is_archived && (offer.stock === null || offer.stock > 0));

                    setOffers(activeOffers);

                    // Initialize quantities to 0 for all offers
                    const initialQuantities = {};
                    activeOffers.forEach(offer => {
                        initialQuantities[offer.id] = 0;
                    });
                    setQuantities(initialQuantities);
                })
                .catch(error => {
                    console.error('Error loading offers:', error);
                    message.error('Kon producten niet laden');
                })
                .finally(() => {
                    setLoadingOffers(false);
                });
        }
    }, []);

    const handleQuantityChange = (offerId, value) => {
        setQuantities(prev => ({
            ...prev,
            [offerId]: value || 0
        }));
    };

    const calculateTotal = () => {
        return offers.reduce((sum, offer) => {
            const quantity = quantities[offer.id] || 0;
            return sum + (offer.price * quantity);
        }, 0);
    };

    const getOrderLines = () => {
        return offers
            .filter(offer => quantities[offer.id] > 0)
            .map(offer => ({
                offer_id: offer.id,
                quantity: quantities[offer.id]
            }));
    };

    const handleSubmit = async (values) => {
        const orderLines = getOrderLines();

        if (orderLines.length === 0) {
            message.error('Voeg minimaal één product toe aan de bestelling (vul aantal > 0 in)');
            return;
        }

        setLoading(true);

        try {
            const orderData = {
                name: values.name,
                email: values.email,
                delivery_method: values.delivery_method,
                phone: values.phone || null,
                address: values.address || null,
                pick_up_date_time: values.pick_up_date_time || null,
                lines: orderLines
            };

            const response = await api.createManualOrder(orderData);

            message.success('Handmatige bestelling succesvol aangemaakt!');

            // Navigate to customer order status page
            navigate(`/customer/order/status/${response.order_id}/${response.secret}`);
        } catch (error) {
            console.error('Error creating manual order:', error);
            message.error('Fout bij het aanmaken van de bestelling');
        } finally {
            setLoading(false);
        }
    };


    const offersColumns = [
        {
            title: 'Product',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Prijs',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `€${price.toFixed(2)}`
        },
        {
            title: 'Aantal',
            key: 'quantity',
            render: (_, offer) => (
                <InputNumber
                    min={0}
                    value={quantities[offer.id] || 0}
                    onChange={(value) => handleQuantityChange(offer.id, value)}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Totaal',
            key: 'total',
            render: (_, offer) => {
                const quantity = quantities[offer.id] || 0;
                return `€${(offer.price * quantity).toFixed(2)}`;
            }
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col flex="auto">
                    <h1>Handmatige Bestelling Aanmaken</h1>
                    <p>Maak een bestelling aan zonder €1 transactiekosten. De klant ontvangt een betaallink.</p>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        Bestelling Aanmaken
                    </Button>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24} lg={12}>
                    <Card title="Klantgegevens">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                label="Naam klant"
                                name="name"
                                rules={[{ required: true, message: 'Voer de naam van de klant in' }]}
                            >
                                <Input placeholder="Jan Jansen" />
                            </Form.Item>

                            <Form.Item
                                label="Email klant"
                                name="email"
                                rules={[
                                    { required: true, message: 'Voer het email adres in' },
                                    { type: 'email', message: 'Voer een geldig email adres in' }
                                ]}
                            >
                                <Input placeholder="jan@voorbeeld.nl" />
                            </Form.Item>

                            <Form.Item
                                label="Telefoonnummer"
                                name="phone"
                            >
                                <Input placeholder="06-12345678" />
                            </Form.Item>

                            <Form.Item
                                label="Bezorgmethode"
                                name="delivery_method"
                                rules={[{ required: true, message: 'Selecteer een bezorgmethode' }]}
                            >
                                <Select placeholder="Selecteer bezorgmethode">
                                    <Option value="pickup_slot">Afhalen op tijdslot</Option>
                                    <Option value="appointment">Op afspraak</Option>
                                    <Option value="stall">Vanuit kraam</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Adres"
                                name="address"
                            >
                                <Input placeholder="Straatnaam 123, 1234 AB Stad" />
                            </Form.Item>

                            <Form.Item
                                label="Datum/tijd"
                                name="pick_up_date_time"
                            >
                                <Input placeholder="Maandag 12 januari, 14:00" />
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card
                        title="Producten"
                        extra={
                            <div style={{ fontWeight: 'normal' }}>
                                Totaal: <strong>€{calculateTotal().toFixed(2)}</strong>
                                <div style={{ fontSize: '12px', color: '#999' }}>
                                    * Geen transactiekosten
                                </div>
                            </div>
                        }
                    >
                        {loadingOffers ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Spin size="large" />
                                <p style={{ marginTop: 16, color: '#999' }}>Producten laden...</p>
                            </div>
                        ) : offers.length > 0 ? (
                            <>
                                <Table
                                    dataSource={offers}
                                    columns={offersColumns}
                                    pagination={false}
                                    size="small"
                                    rowKey="id"
                                />
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                    onClick={() => form.submit()}
                                    style={{ marginTop: 16 }}
                                >
                                    Bestelling Aanmaken
                                </Button>
                            </>
                        ) : (
                            <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                                Je hebt nog geen actieve producten in je winkel.
                                <br />
                                <a href="/offers">Voeg eerst producten toe</a>
                            </p>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
